import React, { useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { useAppSelector } from 'app-components/hooks/redux';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import GlobalHotKeysHelp from './GlobalHotKeysHelp';

export type GlobalHotKey = {
  code: string;

  shiftKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;

  description: string;
  callback: () => void;
  context?: string;
};
const globalHotKeys: GlobalHotKey[] = [];

export const HELP_HOLD_DELAY_MS = 500;

const isHelpModifierKey = (key: string): boolean => key === 'Alt' || key === 'Shift';

export const useGlobalHotKey = (globalHotKey: GlobalHotKey) => {
  useDeepCompareEffect(() => {
    globalHotKeys.push(globalHotKey);
    return () => {
      const index = globalHotKeys.indexOf(globalHotKey);
      if (index !== -1) {
        globalHotKeys.splice(index, 1);
      }
    };
  }, [globalHotKey]);
};

const GlobalHotKeys = () => {
  const globalHotKeysEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'globalHotKeys'));
  const [helpHotKeys, setHelpHotKeys] = useState<GlobalHotKey[] | null>(null);

  useEffect(() => {
    if (globalHotKeysEnabled) {
      let helpTimer: ReturnType<typeof setTimeout> | undefined;
      const hideHelp = () => {
        clearTimeout(helpTimer);
        helpTimer = undefined;
        setHelpHotKeys(null);
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        const isHelpChord =
          isHelpModifierKey(event.key) && event.altKey && event.shiftKey && !event.ctrlKey && !event.metaKey;
        if (!isHelpChord) {
          hideHelp();
        } else if (!helpTimer) {
          helpTimer = setTimeout(() => {
            setHelpHotKeys([...globalHotKeys].sort((a, b) => a.description.localeCompare(b.description)));
          }, HELP_HOLD_DELAY_MS);
        }

        globalHotKeys.forEach((hotKey) => {
          if (
            event.code === hotKey.code &&
            Boolean(event.shiftKey) === Boolean(hotKey.shiftKey) &&
            Boolean(event.ctrlKey) === Boolean(hotKey.ctrlKey) &&
            Boolean(event.altKey) === Boolean(hotKey.altKey) &&
            Boolean(event.metaKey) === Boolean(hotKey.metaKey)
          ) {
            if (hotKey.callback) {
              event.preventDefault();
              if (!event.repeat) {
                hotKey.callback();
              }
            }
          }
        });
      };
      const handleKeyUp = (event: KeyboardEvent) => {
        if (isHelpModifierKey(event.key)) {
          hideHelp();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keyup', handleKeyUp);
      window.addEventListener('blur', hideHelp);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('keyup', handleKeyUp);
        window.removeEventListener('blur', hideHelp);
        hideHelp();
      };
    }
    return undefined;
  }, [globalHotKeysEnabled]);

  return helpHotKeys?.length ? <GlobalHotKeysHelp hotKeys={helpHotKeys} /> : null;
};

export default GlobalHotKeys;
